import React, { useState } from 'react';
import { Flex, Text, Box } from '@chakra-ui/react';
import { FiArrowUpRight, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ff } from '../../../../theme';

const DISMISS_DURATION_MS = 2 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'promo_bounty_dismissed';

const DismissButton: React.FC<{ onClick: (e: React.MouseEvent) => void; color: string }> = ({ onClick, color }) => (
  <Box
    as="button"
    type="button"
    aria-label="Dismiss"
    onClick={onClick}
    w="44px"
    h="44px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    color={color}
    flex="none"
  >
    <FiX size={14} strokeWidth={2.6} />
  </Box>
);

/** Home promo card (board: 272px tile, white icon square, dismiss in the corner). */
export const BannerCard: React.FC<{
  bg: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  onDismiss: (e: React.MouseEvent) => void;
}> = ({ bg, icon, title, subtitle, onClick, onDismiss }) => (
  <Flex
    position="relative"
    flex="none"
    w="272px"
    p="14px 40px 14px 14px"
    borderRadius="18px"
    align="flex-start"
    gap="12px"
    bg={bg}
    cursor="pointer"
    onClick={onClick}
  >
    <Flex flex="none" w="40px" h="40px" borderRadius="12px" bg="#FFFFFF" align="center" justify="center" color={ff.ink}>
      {icon}
    </Flex>
    {/* Click bubbles to the card; the button gives keyboard access. */}
    <Box as="button" type="button" flex={1} minW={0} display="flex" flexDirection="column" gap="3px" textAlign="left" color={ff.ink}>
      <Text fontSize="15px" fontWeight={600}>{title}</Text>
      <Text fontSize="13px" lineHeight="1.35" color={ff.body}>{subtitle}</Text>
    </Box>
    <Box position="absolute" top={0} right={0}>
      <DismissButton onClick={onDismiss} color="#55555A" />
    </Box>
  </Flex>
);

export default function PromoBanner() {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return false;
      const dismissedUntil = parseInt(stored, 10);
      return dismissedUntil > Date.now();
    } catch {
      return false;
    }
  });

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    const dismissedUntil = Date.now() + DISMISS_DURATION_MS;
    try {
      localStorage.setItem(STORAGE_KEY, String(dismissedUntil));
      setIsDismissed(true);
    } catch (err) {
      console.error('Failed to save promo dismiss:', err);
    }
  };

  if (isDismissed) return null;

  // Board: full-width lime pill, tilted ink "Bounty" tag over the top edge, ink arrow, corner dismiss.
  return (
    <Flex
      position="relative"
      flex="none"
      w="100%"
      minH="76px"
      mt="10px"
      p="16px 12px 14px 20px"
      borderRadius="28px"
      align="center"
      gap="12px"
      bg={ff.lime}
      color={ff.ink}
      cursor="pointer"
      onClick={() => navigate('/bounties')}
    >
      <Text
        as="span"
        position="absolute"
        top="-11px"
        left="16px"
        transform="rotate(-4deg)"
        px="8px"
        py="3px"
        borderRadius="6px"
        bg={ff.ink}
        color={ff.lime}
        textStyle="eyebrow"
        fontSize="10px"
        lineHeight="normal"
      >
        Bounty
      </Text>
      {/* Click bubbles to the card; the button gives keyboard access. */}
      <Box as="button" type="button" flex={1} minW={0} display="flex" flexDirection="column" gap="2px" textAlign="left">
        <Text fontSize="16px" fontWeight={600} lineHeight="1.25">Know someone at a brand?</Text>
        <Text fontSize="14px" lineHeight="1.3" color={ff.body}>Make the intro, get paid.</Text>
      </Box>
      <Flex flex="none" w="44px" h="44px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center" aria-hidden>
        <FiArrowUpRight size={18} strokeWidth={2.4} />
      </Flex>
      <Box position="absolute" top="-14px" right="-14px">
        <Box
          as="button"
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          w="44px"
          h="44px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex w="24px" h="24px" borderRadius="full" bg={ff.grey} border="2px solid white" color={ff.body} align="center" justify="center">
            <FiX size={12} strokeWidth={2.8} />
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
